import { useMemo } from 'react';
import { useFilterStoreWithMomentDates } from '../../../store/filterStore.js';

/**
 * useWidgetConfig - Custom hook to centralize config/date/filter handling for widgets
 * 
 * This hook standardizes the pattern of:
 * 1. Stabilizing filters to prevent re-fetch loops
 * 2. Merging date range from the filter store into API request bodies
 * 3. Adding additional body parameters as needed
 * 
 * @param {Object} config - Widget configuration object
 * @param {Array} filters - Runtime filters array
 * @param {Object} options - Hook options
 * @param {boolean} options.includeDates - Whether to include start_date/end_date in request body (default: true)
 * @param {Object} options.additionalBody - Additional properties to merge into request body
 * @param {boolean} options.addDetailedResults - Whether to add add_detailed_results flag (default: false)
 * @param {boolean} options.addTrendData - Whether to add add_trend_data flag (default: false)
 * @returns {Object} - { finalConfig, stableFilters, startDate, endDate }
 */
const useWidgetConfig = (config, filters = [], options = {}) => {
  const { startDate, endDate } = useFilterStoreWithMomentDates();
  
  const { 
    includeDates = true, 
    additionalBody = {},
    addDetailedResults = false,
    addTrendData = false
  } = options;

  // Stabilize filters to avoid re-fetch loops when filters is undefined or recreated
  const stableFilters = useMemo(() => filters || [], [filters]);

  // Build a non-mutating config with updated dates merged into the request body
  const finalConfig = useMemo(() => {
    const dataSource = config?.dataSource;
    
    // If no dataSource, return config as-is (query won't run anyway)
    if (!dataSource) {
      console.warn('useWidgetConfig: No dataSource in config', { configId: config?.id });
      return config;
    }
    
    const baseBody = dataSource.body || {};
    
    // Build the body with optional date injection
    let mergedBody = { ...baseBody };
    
    // Add dates if enabled and both dates are present
    if (includeDates && startDate && endDate) {
      mergedBody = {
        ...mergedBody,
        start_date: startDate.format("YYYY-MM-DD"),
        end_date: endDate.format("YYYY-MM-DD"),
      };
    }
    
    // Add detailed results flag if specified
    if (addDetailedResults !== undefined) {
      mergedBody.add_detailed_results = addDetailedResults;
    }
    
    // Add trend data flag if specified (preserve explicit false)
    if (addTrendData !== undefined) {
      mergedBody.add_trend_data = addTrendData;
    }
    
    // Merge any additional body parameters
    if (Object.keys(additionalBody).length > 0) {
      mergedBody = { ...mergedBody, ...additionalBody };
    }

    return {
      ...config,
      dataSource: {
        ...dataSource,
        method: dataSource.method || 'POST',
        body: mergedBody
      }
    };
  }, [config, startDate, endDate, includeDates, additionalBody, addDetailedResults, addTrendData]);

  return { 
    finalConfig, 
    stableFilters, 
    startDate, 
    endDate 
  };
};

export default useWidgetConfig;

